import {
  IMAGE_MAX_EDGE,
  VIDEO_FRAME_MAX_EDGE,
  planFrameTimes,
  scaleToLongEdge,
  type FrameSource,
} from '../../lib/portalInspiration/draft';

// PORTAL INSPIRATION — BROWSER FRAME CAPTURE for the AI draft (Run I, brief §4).
//
// ⚠️ THE MODEL'S EYES ARE THE BROWSER'S. Captured here at upload/draft time so there is no ffmpeg
// in the app and nothing changes in the worker. Order of attempts (a pure chain, `captureWithFallbacks`):
//   1. the LOCAL FILE (only when it is still in memory, i.e. just uploaded in this tab, and it decodes);
//   2. the processed 720p H.264 PREVIEW through the SAME-ORIGIN staff proxy (a CDN with no CORS headers
//      taints the canvas, so the CDN URL is no longer used for frames);
//   3. the POSTER still only (a single frame - the model is told it cannot see time).
// ⚠️ PHOTOS ARE NOT CAPTURED HERE ANY MORE: the server reads the rendition from storage itself.
// The result always records `source` and how many frames were captured, so the reviewer sees
// "AI saw N frames" and a poster-only draft says so.

export type CapturedFrames = {
  frames: Array<{ base64: string; timeSec: number }>;
  durationSec: number | null;
  source: FrameSource;
};

export class CaptureError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CaptureError';
  }
}

/** Try each attempt in order; the first that yields at least one frame wins. Pure, so it is tested in node. */
export async function captureWithFallbacks(
  attempts: ReadonlyArray<() => Promise<CapturedFrames>>,
): Promise<CapturedFrames> {
  let last: unknown = null;
  for (const attempt of attempts) {
    try {
      const r = await attempt();
      if (r.frames.length > 0) return r;
    } catch (err) {
      last = err;
    }
  }
  throw new CaptureError(
    last instanceof Error && last.message
      ? `Couldn't read this video in your browser (${last.message}). If it was only just uploaded, it may still be processing - try again in a minute.`
      : "Couldn't read this video in your browser. If it was only just uploaded, it may still be processing - try again in a minute.",
  );
}

/**
 * Grab one frame per planned time from anything that can seek. `grab` returns base64 JPEG or throws.
 * ⚠️ A frame that fails is SKIPPED rather than aborting the clip (a flaky seek must not lose the other 29),
 * but if fewer than half survive the capture fails so the next fallback is tried.
 */
export async function captureTimes(
  times: readonly number[],
  grab: (timeSec: number) => Promise<string>,
): Promise<Array<{ base64: string; timeSec: number }>> {
  const out: Array<{ base64: string; timeSec: number }> = [];
  for (const t of times) {
    try {
      out.push({ base64: await grab(t), timeSec: t });
    } catch {
      /* skipped */
    }
  }
  if (out.length * 2 < times.length) throw new CaptureError('Too many frames could not be read.');
  return out;
}

// ---------- DOM glue (not unit-tested: it needs a real decoder) ----------

const JPEG_QUALITY = 0.8;

function toBase64Jpeg(source: CanvasImageSource, w: number, h: number, maxEdge: number): string {
  const size = scaleToLongEdge(w, h, maxEdge);
  const canvas = document.createElement('canvas');
  canvas.width = size.w;
  canvas.height = size.h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new CaptureError('No canvas available.');
  ctx.drawImage(source, 0, 0, size.w, size.h);
  // ⚠️ Throws a SecurityError when the canvas is tainted (a cross-origin source without CORS) - the fallback chain catches it.
  const url = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
  return url.slice(url.indexOf(',') + 1);
}

function loadImage(src: string, crossOrigin: boolean): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (crossOrigin) img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new CaptureError('The image could not be decoded.'));
    img.src = src;
  });
}

function loadVideo(src: string, crossOrigin: boolean): Promise<HTMLVideoElement> {
  return new Promise((resolve, reject) => {
    const v = document.createElement('video');
    v.muted = true;
    v.preload = 'auto';
    v.playsInline = true;
    if (crossOrigin) v.crossOrigin = 'anonymous';
    const timer = setTimeout(() => reject(new CaptureError('The video took too long to load.')), 30_000);
    v.onloadeddata = () => {
      clearTimeout(timer);
      if (!v.videoWidth || !v.videoHeight || !Number.isFinite(v.duration)) {
        reject(new CaptureError('The video could not be decoded.'));
      } else resolve(v);
    };
    v.onerror = () => {
      clearTimeout(timer);
      reject(new CaptureError('The video could not be decoded.'));
    };
    v.src = src;
  });
}

function seek(v: HTMLVideoElement, t: number): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new CaptureError('Seek timed out.')), 8_000);
    v.onseeked = () => {
      clearTimeout(timer);
      resolve();
    };
    v.onerror = () => {
      clearTimeout(timer);
      reject(new CaptureError('Seek failed.'));
    };
    v.currentTime = Math.min(Math.max(0, t), Math.max(0, v.duration - 0.05));
  });
}

/** Frames at 2/s (min 8, cap 40) from a video File or URL, long edge VIDEO_FRAME_MAX_EDGE. */
export async function captureVideo(source: { file: Blob } | { url: string }): Promise<CapturedFrames> {
  const isFile = 'file' in source;
  const objectUrl = isFile ? URL.createObjectURL(source.file) : null;
  try {
    const v = await loadVideo(isFile ? objectUrl! : source.url, !isFile);
    const times = planFrameTimes(v.duration);
    const frames = await captureTimes(times, async (t) => {
      await seek(v, t);
      return toBase64Jpeg(v, v.videoWidth, v.videoHeight, VIDEO_FRAME_MAX_EDGE);
    });
    return { frames, durationSec: v.duration, source: isFile ? 'file' : 'preview' };
  } finally {
    if (objectUrl) URL.revokeObjectURL(objectUrl);
  }
}

/** Last resort: the poster still. ⚠️ One frame, no duration - the server tells the model it cannot see time. */
export async function capturePoster(url: string, durationSec: number | null): Promise<CapturedFrames> {
  const img = await loadImage(url, true);
  return { frames: [{ base64: toBase64Jpeg(img, img.naturalWidth, img.naturalHeight, VIDEO_FRAME_MAX_EDGE), timeSec: 0 }], durationSec, source: 'poster' };
}

/** What the page knows about an entry's video when drafting. `file` is present only just after an upload in this tab. */
export type MediaForDraft = {
  mediaType: 'video';
  file?: Blob | null;
  entryId: string;
  /** The same-origin staff proxy for the 720p preview mp4, as a Blob. */
  fetchPreviewVideo: (entryId: string) => Promise<Blob>;
  posterUrl: string | null;
  durationMs: number | null;
};

export function captureForDraft(m: MediaForDraft): Promise<CapturedFrames> {
  const attempts: Array<() => Promise<CapturedFrames>> = [];
  if (m.file) attempts.push(() => captureVideo({ file: m.file! }));
  attempts.push(async () => {
    const r = await captureVideo({ file: await m.fetchPreviewVideo(m.entryId) });
    return { ...r, source: 'preview' as const };
  });
  if (m.posterUrl) attempts.push(() => capturePoster(m.posterUrl!, m.durationMs === null ? null : m.durationMs / 1000));
  return captureWithFallbacks(attempts);
}
